import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Typography,
  Box,
  Alert,
  Link,
} from "@mui/material";
import useAxios from "../../../../hooks/useAxios";
import "./ImportStudentsModal.css";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';

const ImportStudentsModal = ({ open, onClose }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(null);
  const axiosInstance = useAxios();

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ['text/csv', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
      if (!validTypes.includes(file.type)) {
        setError("Please select a valid CSV or Excel file (.csv, .xlsx, .xls)");
        setSelectedFile(null);
        return;
      }
      
      // Validate file size (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        setError("File size must be less than 10MB");
        setSelectedFile(null);
        return;
      }
      
      setSelectedFile(file);
      setError(null);
    }
  };

  const handleSubmit = async () => {
    if (!selectedFile) {
      setError("Please select a file to upload");
      return;
    }

    setError(null);
    setSuccess(null);
    setLoading(true);
    setUploadProgress("Uploading file...");

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await axiosInstance.post("/student-accounts/bulk-create", formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(`Uploading: ${percentCompleted}%`);
        },
      });

      if (response.data.success) {
        const { successfully_created, total_processed, errors } = response.data.data;
        
        setSuccess(`Successfully created ${successfully_created} out of ${total_processed} student accounts.`);
        setTimeout(() => { onClose(true); }, 3000); // Refresh table
      } else {
        // Handle specific error codes
        if (response.data.error_code === 'STUDENT_LIMIT_EXCEEDED') {
          setError(
            <Box>
              <Typography variant="body2" color="error" gutterBottom>
                {response.data.message}
              </Typography>
              <Typography variant="body2" color="error">
                Please upgrade your plan or reduce the number of students in your file.
              </Typography>
              <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                <strong>Current capacity:</strong> {response.data.remaining_capacity} students
              </Typography>
            </Box>
          );
        } else if (response.data.error_code === 'NO_SUBSCRIPTION') {
          setError(
            <Box>
              <Typography variant="body2" color="error" gutterBottom>
                {response.data.message}
              </Typography>
              <Typography variant="body2" color="error">
                Please purchase a subscription plan to create student accounts.
              </Typography>
            </Box>
          );
        } else {
          setError(response.data.message || "Failed to import students.");
        }
      }
    } catch (err) {
      setError(err.response?.data?.message || "An unexpected error occurred.");
    } finally {
      setLoading(false);
      setUploadProgress(null);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setError(null);
    setSuccess(null);
    setUploadProgress(null);
    onClose(false);
  };

  const downloadSampleFile = () => {
    // Create a sample CSV content
    const csvContent = "John Smith\nJane Doe\nMichael Johnson\nEmily Davis";
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sample_student_import.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle className="modal-title">
        Import Students from File
      </DialogTitle>
      
      <DialogContent>
        <Typography className="modal-description">
          Upload a CSV or Excel file to create multiple student accounts at once.
        </Typography>

        <Box className="file-requirements">
          <Typography variant="h6" className="requirements-title">
            File Requirements:
          </Typography>
          {/* <Typography variant="body2" className="requirement-item">
            • File format: CSV (.csv), Excel (.xlsx, .xls)
          </Typography>
          <Typography variant="body2" className="requirement-item">
            • Maximum file size: 10MB
          </Typography> */}
          <Typography variant="body2" className="requirement-item">
            • The file should only contain student names, no headers
          </Typography>
          <Typography variant="body2" className="requirement-item">
            • Each row should contain one student name
          </Typography>
        </Box>

        <Box className="sample-file-section">
          <Typography variant="body2" className="sample-text">
            Download a sample file to see the correct format:
          </Typography>
          <Button
            startIcon={<DownloadIcon />}
            onClick={downloadSampleFile}
            className="download-sample-button"
            size="small"
          >
            Download Sample CSV
          </Button>
        </Box>

        <Box className="file-upload-section">
          <input
            accept=".csv,.xlsx,.xls"
            style={{ display: 'none' }}
            id="file-upload"
            type="file"
            onChange={handleFileSelect}
            disabled={loading}
          />
          <label htmlFor="file-upload">
            <Button
              variant="outlined"
              component="span"
              startIcon={<CloudUploadIcon />}
              className="file-upload-button"
              disabled={loading}
            >
              Choose File
            </Button>
          </label>
          
          {selectedFile && (
            <Box className="selected-file-info">
              <Typography variant="body2" className="file-name">
                Selected: {selectedFile.name}
              </Typography>
              <Typography variant="body2" className="file-size">
                Size: {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
              </Typography>
            </Box>
          )}
        </Box>

        {error && (
          <Alert severity="error" className="error-alert">
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" className="success-alert">
            {success}
          </Alert>
        )}

        {uploadProgress && (
          <Box className="upload-progress">
            <CircularProgress size={20} />
            <Typography variant="body2" className="progress-text">
              {uploadProgress}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions className="modal-actions">
        <Button 
          onClick={handleClose} 
          disabled={loading}
          className="secondary-button"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={loading || !selectedFile}
          startIcon={loading && <CircularProgress size={20} />}
          className="primary-button"
        >
          {loading ? "Importing..." : "Import Students"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImportStudentsModal; 